import { useCompanyLabels } from '../context/CompanyContext';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowUpDown, Download, Filter, Plus } from 'lucide-react';
import Button from '../components/atoms/Button';
import EmptyState from '../components/atoms/EmptyState';
import Loading from '../components/atoms/Loading';
import { fetchQueries } from '../redux/complaintmanagement/actions';
import { fetchComplaintSummary, fetchStatuses } from '../redux/status/actions';
import { fetchWorkflowSteps } from '../redux/Workflow/actions';
import { changeComplaintStatus } from '../redux/changestatus/actions';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { setId } from '../redux/appDetails/reducer';
import { resetComplaintManagementState } from '../redux/complaintmanagement/reducers';
import { resetStatusState } from '../redux/status/reducers';
import { resetChangeStatusState } from '../redux/changestatus/reducers';

const COMPLAINT_TYPE_STORAGE_KEY = 'aira-query-complaintTypeId';
// Unused: default labeled-column id. Table columns now come from complaint rows.
// const labelFieldId = 44;
const fallbackStatusNames = ['Open', 'Closed'];
const fallbackEscalationLabels = ['Level 1', 'Level 2', 'Level 3'];
const STATUS_FIELD = { id: 'status', label: 'Status', key: 'status' };
const ESCALATION_FIELD = { id: 'escalationTo', label: 'Escalation To', key: 'escalationTo' };
const SUBMITTED_BY_FIELD = { id: 'submittedByUserName', label: 'Submitted By', key: 'submittedByUserName' };
const UPDATED_AT_FIELD = { id: 'updatedAt', label: 'Updated At', key: 'updatedAt' };
const UPDATED_AT_SORT_NEWEST = 'desc';
const UPDATED_AT_SORT_OLDEST = 'asc';
const RESERVED_TABLE_KEYS = new Set(['status', 'escalationTo', 'id', 'submittedByUserName', 'updatedAt']);
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function formatComplaintDateTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}, ${hours}:${minutes}`;
}

function parseUpdatedAtTime(value) {
  if (!value) return 0;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
}

function sortComplaintRowsByUpdatedAt(rows, sortDirection) {
  if (sortDirection !== UPDATED_AT_SORT_NEWEST && sortDirection !== UPDATED_AT_SORT_OLDEST) {
    return rows;
  }
  return [...rows].sort((left, right) => {
    const leftTime = parseUpdatedAtTime(left.updatedAtValue);
    const rightTime = parseUpdatedAtTime(right.updatedAtValue);
    if (leftTime === rightTime) return 0;
    return sortDirection === UPDATED_AT_SORT_OLDEST
      ? leftTime - rightTime
      : rightTime - leftTime;
  });
}

function getNextUpdatedAtSortDirection(currentDirection) {
  if (currentDirection === UPDATED_AT_SORT_NEWEST) return UPDATED_AT_SORT_OLDEST;
  return UPDATED_AT_SORT_NEWEST;
}

function buildComplaintTableFields(formFieldColumns) {
  return [SUBMITTED_BY_FIELD, UPDATED_AT_FIELD, ...formFieldColumns, STATUS_FIELD, ESCALATION_FIELD];
}

function getComplaintTypeId(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'object') return value.data ?? value.id ?? value._id ?? null;
  return value;
}

function readStoredComplaintTypeId() {
  try {
    return sessionStorage.getItem(COMPLAINT_TYPE_STORAGE_KEY);
  } catch {
    return null;
  }
}

function saveComplaintTypeId(value) {
  const id = getComplaintTypeId(value);
  if (id == null || id === '') return null;
  const normalized = String(id);
  try {
    sessionStorage.setItem(COMPLAINT_TYPE_STORAGE_KEY, normalized);
  } catch {
    // Ignore storage failures (private mode / disabled storage).
  }
  return normalized;
}

function getFirstAvailableComplaintTypeId(...values) {
  for (const value of values) {
    const resolved = getComplaintTypeId(value);
    if (resolved != null && resolved !== '') return String(resolved);
  }
  return null;
}

function getComplaintStatusName(complaint, statusList = []) {
  const raw = complaint?.status;
  if (typeof raw === 'string' && raw.trim()) return raw.trim();
  if (raw && typeof raw === 'object') {
    const name = raw.name ?? raw.label ?? raw.status;
    if (name) return String(name).trim();
    const nestedId = raw.id ?? raw.statusId;
    if (nestedId != null) {
      const match = statusList.find((item) => String(item.id) === String(nestedId));
      if (match?.name) return match.name;
    }
  }
  const statusId = complaint?.statusId ?? complaint?.complaintStatusId;
  if (statusId != null) {
    const match = statusList.find((item) => String(item.id) === String(statusId));
    if (match?.name) return match.name;
  }
  return '';
}

function getComplaintStatusId(complaint, statusList = []) {
  const direct = complaint?.statusId ?? complaint?.complaintStatusId;
  if (direct != null && direct !== '') return direct;
  const raw = complaint?.status;
  if (raw && typeof raw === 'object') {
    const nestedId = raw.id ?? raw.statusId;
    if (nestedId != null && nestedId !== '') return nestedId;
  }
  if (typeof raw === 'string' && raw.trim()) {
    const match = statusList.find((item) => String(item.name).trim() === raw.trim());
    if (match?.id != null) return match.id;
  }
  return null;
}

function isEnabledFlag(value) {
  return value === true || value === 'Yes' || value === 'true' || value === 1 || value === '1';
}

function isResolvedOrClosedStatus(name) {
  const key = String(name || '').trim().toLowerCase();
  return key === 'resolved' || key === 'closed';
}

function getCurrentWorkflowStep(complaint, steps = []) {
  const stepId =
    complaint?.currentStepId ??
    complaint?.workflowStepId ??
    complaint?.currentWorkflowStepId;
  if (stepId != null && stepId !== '') {
    const match = steps.find((step) => String(step.id) === String(stepId));
    if (match) return match;
  }
  const level = complaint?.level ?? complaint?.levelNo;
  if (level != null && level !== '') {
    return steps.find((step) => String(step.levelNo) === String(level)) ?? null;
  }
  return null;
}

function getEscalationLevelLabel(complaint, steps = []) {
  const level = complaint?.level ?? complaint?.levelNo;
  if (level != null && level !== '') return `Level ${level}`;
  const stepId = complaint?.workflowStepId ?? complaint?.currentWorkflowStepId ?? complaint?.currentStepId;
  if (stepId != null) {
    const match = steps.find((step) => String(step.id) === String(stepId));
    if (match?.levelNo != null) return `Level ${match.levelNo}`;
  }
  return '';
}

function mapComplaintToTableRow(complaint, statusList = [], steps = []) {
  const currentStep = getCurrentWorkflowStep(complaint, steps);
  const row = {
    id: complaint?.complaintId ?? complaint?.id,
    status: getComplaintStatusName(complaint, statusList),
    statusId: getComplaintStatusId(complaint, statusList),
    escalationTo: getEscalationLevelLabel(complaint, steps),
    currentStepId: currentStep?.id ?? complaint?.currentStepId ?? complaint?.workflowStepId ?? complaint?.currentWorkflowStepId ?? null,
    canResolve: isEnabledFlag(complaint?.canResolve ?? currentStep?.canResolve),
    // Unused: this table never reads canReassign.
    // canReassign: isEnabledFlag(complaint?.canReassign ?? currentStep?.canReassign),
    isFinalLevel: isEnabledFlag(
      complaint?.isFinalLevel ?? currentStep?.isFinalLevel ?? currentStep?.finalLevel,
    ),
    submittedByUserName: complaint?.submittedByUserName || '-',
    updatedAt: formatComplaintDateTime(complaint?.updatedAt) || '-',
    updatedAtValue: complaint?.updatedAt ?? '',
  };
  (complaint?.formField || []).forEach((field) => {
    const key = field.fieldKey || field.fieldName;
    if (!key || RESERVED_TABLE_KEYS.has(key)) return;
    row[key] = field.value;
  });
  return row;
}

function getComplaintCellClassName(key, isHeader = false) {
  const classes = [];
  if (!isHeader && key === 'description') classes.push('ca-complaint-table__desc');
  if (key === 'status') classes.push('ca-complaint-table__sticky', 'ca-complaint-table__sticky--status');
  if (key === 'escalationTo') classes.push('ca-complaint-table__sticky', 'ca-complaint-table__sticky--escalation');
  return classes.length ? classes.join(' ') : undefined;
}

function ComplaintTableSelect({ value, options, fallbackOptions, ariaLabel, selectKey, onChange }) {
  const source = options.length ? options : fallbackOptions.map((name) => ({ value: name, label: name }));
  const list = source.map((item) => {
    if (item && typeof item === 'object') {
      return {
        value: String(item.value ?? item.id ?? ''),
        label: item.label ?? item.name ?? String(item.value ?? item.id ?? ''),
        disabled: Boolean(item.disabled),
      };
    }
    return { value: String(item), label: String(item), disabled: false };
  }).filter((item) => item.value || item.label);
  const selected = value != null && value !== '' && value !== '-' ? String(value) : '';
  const values = list.map((item) => item.value);
  const withSelected = selected && !values.includes(selected)
    ? [{ value: selected, label: selected, disabled: false }, ...list]
    : list;
  const shown = withSelected.some((item) => item.value === selected)
    ? selected
    : (withSelected[0]?.value ?? '');

  return (
    <select
      className="ca-complaint-table__select"
      aria-label={ariaLabel}
      value={shown}
      onChange={(event) => onChange?.(event.target.value)}
      key={selectKey}
    >
      {withSelected.map((option) => (
        <option key={option.value} value={option.value} disabled={option.disabled}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

function getStatusDropdownOptions(statusList, row) {
  return statusList.map((item) => {
    const resolveLike = isResolvedOrClosedStatus(item.name);
    const isCurrent = String(item.id) === String(row.statusId);
    let disabled = false;
    if (resolveLike && !row.canResolve) disabled = true;
    if (row.isFinalLevel && !resolveLike) disabled = true;
    if (isCurrent) disabled = false;
    return { value: item.id, label: item.name, disabled };
  }).filter((item) => item.label);
}

function getEscalationDropdownOptions(steps, row) {
  const sorted = [...steps].sort((left, right) => (left.levelNo ?? 0) - (right.levelNo ?? 0));
  const current = sorted.find((step) => String(step.id) === String(row.currentStepId))
    ?? sorted.find((step) => `Level ${step.levelNo}` === row.escalationTo);
  const currentLevel = current?.levelNo;
  const nextLevel = currentLevel != null ? currentLevel + 1 : null;

  return sorted.map((step) => {
    const isCurrent = current != null && String(step.id) === String(current.id);
    const isNext = nextLevel != null && step.levelNo === nextLevel;
    const isFirstWithoutCurrent = current == null && step.levelNo === sorted[0]?.levelNo;
    let disabled = true;
    if (isCurrent) disabled = false;
    else if (!row.isFinalLevel && (isNext || isFirstWithoutCurrent)) disabled = false;
    return {
      value: step.id,
      label: step.levelNo != null ? `Level ${step.levelNo}` : String(step.name || step.id),
      disabled,
    };
  }).filter((item) => item.label);
}

function dropdownContainsValue(options, value) {
  if (value == null || value === '' || !Array.isArray(options)) return false;
  return options.some((item) => {
    if (item && typeof item === 'object') {
      return String(item.value ?? item.id) === String(value);
    }
    return String(item) === String(value);
  });
}

function renderComplaintTableCell(field, row, statusNames = fallbackStatusNames, escalationLabels = fallbackEscalationLabels, ctx = {}) {
  const value = row[field.key] ?? '-';
  const statusOptionsList = ctx.statusOptions ?? statusNames;
  const escalationOptionsList = ctx.escalationOptions ?? escalationLabels;

  if (field.key === 'status') {
    return (
      <ComplaintTableSelect
        value={dropdownContainsValue(statusOptionsList, row.statusId) ? row.statusId : value}
        options={statusOptionsList}
        fallbackOptions={fallbackStatusNames}
        ariaLabel="Status"
        selectKey={`status-${row.id}`}
        onChange={ctx.onStatusChange}
      />
    );
  }

  if (field.key === 'escalationTo') {
    return (
      <ComplaintTableSelect
        value={dropdownContainsValue(escalationOptionsList, row.currentStepId) ? row.currentStepId : value}
        options={escalationOptionsList}
        fallbackOptions={fallbackEscalationLabels}
        ariaLabel="Escalation to"
        selectKey={`escalation-${row.id}`}
        onChange={ctx.onEscalationChange}
      />
    );
  }

  return value;
}

export default function ComplaintTable() {
  const { fieldLabel } = useCompanyLabels();
  const dispatch = useDispatch();
  const { complaintTypeId: routeComplaintTypeId } = useParams();
  const [searchParams] = useSearchParams();
  const storeComplaintTypeId = useSelector((store) => store.appDetails?.id);
  const complaintTypeId = getFirstAvailableComplaintTypeId(
    routeComplaintTypeId,
    searchParams.get('complaintTypeId'),
    storeComplaintTypeId,
    readStoredComplaintTypeId(),
  );
  const {
    list = [],
    isLoading = false,
    error = null,
    hasLoaded = false,
    requestedComplaintTypeId = null,
  } = useSelector((store) => store.complaintManagement ?? {});
  const statusList = useSelector((store) => store.status?.list ?? []);
  const statusUpdateError = useSelector((store) => store.changeStatus?.error ?? null);
  const updatingId = useSelector((store) => store.changeStatus?.updatingId ?? null);
  const statusNames = useMemo(
    () => statusList.map((item) => item.name).filter(Boolean),
    [statusList],
  );
  const workflowSteps = useSelector((store) => store.workflow?.workflowSteps);
  const steps = useMemo(
    () => (Array.isArray(workflowSteps) ? workflowSteps : []),
    [workflowSteps],
  );
  // Unused: status/escalation cells get options from getComplaintCellContext.
  // const escalationLabels = useMemo(() => {
  //   return [...steps]
  //     .sort((left, right) => (left.levelNo ?? 0) - (right.levelNo ?? 0))
  //     .map((step) => (step.levelNo != null ? `Level ${step.levelNo}` : ''))
  //     .filter(Boolean);
  // }, [steps]);

  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState({ complaintTypeId: null, status: 'all' });
  const [updatedAtSortFilter, setUpdatedAtSortFilter] = useState({
    complaintTypeId: null,
    direction: UPDATED_AT_SORT_NEWEST,
  });
  const selectedStatus =
    String(statusFilter.complaintTypeId) === String(complaintTypeId)
      ? statusFilter.status
      : 'all';
  const selectedUpdatedAtSort =
    String(updatedAtSortFilter.complaintTypeId) === String(complaintTypeId)
      ? updatedAtSortFilter.direction
      : UPDATED_AT_SORT_NEWEST;

  useEffect(() => {
    if (!complaintTypeId || routeComplaintTypeId) return undefined;
    saveComplaintTypeId(complaintTypeId);
    navigate(`/complaintmanagement/${complaintTypeId}`, { replace: true });
    return undefined;
  }, [complaintTypeId, navigate, routeComplaintTypeId]);

  useEffect(() => {
    if (!complaintTypeId) return undefined;
    if (!routeComplaintTypeId) return undefined;

    saveComplaintTypeId(complaintTypeId);
    dispatch(setId(complaintTypeId));
    dispatch(resetComplaintManagementState());
    dispatch(resetStatusState());
    dispatch(resetChangeStatusState());

    const complaintsRequest = dispatch(fetchQueries({
      complaintTypeId,
      page: 1,
      limit: 10,
    }));
    const statusesRequest = dispatch(fetchStatuses(complaintTypeId));
    const summaryRequest = dispatch(fetchComplaintSummary(complaintTypeId));
    const workflowRequest = dispatch(fetchWorkflowSteps(complaintTypeId));

    return () => {
      complaintsRequest.abort?.();
      statusesRequest.abort?.();
      summaryRequest.abort?.();
      workflowRequest.abort?.();
    };
  }, [complaintTypeId, dispatch, routeComplaintTypeId]);

  const complaintRows = useMemo(() => {
    if (
      requestedComplaintTypeId != null &&
      complaintTypeId != null &&
      String(requestedComplaintTypeId) !== String(complaintTypeId)
    ) {
      return [];
    }
    return list.map((complaint) => ({
      ...mapComplaintToTableRow(
        complaint,
        statusList,
        Array.isArray(workflowSteps) ? workflowSteps : [],
      ),
      _fields: complaint?.formField || [],
    }));
  }, [complaintTypeId, list, requestedComplaintTypeId, statusList, workflowSteps]);

  const complaintColumns = useMemo(() => {
    const seen = new Set();
    const columns = [];
    complaintRows.forEach((row) => {
      (row._fields || []).forEach((field) => {
        const key = field.fieldKey || field.fieldName;
        if (!key || RESERVED_TABLE_KEYS.has(key) || seen.has(key)) return;
        seen.add(key);
        columns.push({
          id: key,
          label: field.fieldName || key,
          key,
        });
      });
    });
    return columns;
  }, [complaintRows]);

  const tableFields = buildComplaintTableFields(complaintColumns);
  const sortedComplaintRows = useMemo(
    () => sortComplaintRowsByUpdatedAt(complaintRows, selectedUpdatedAtSort),
    [complaintRows, selectedUpdatedAtSort],
  );
  const tableRows = sortedComplaintRows.map(({ _fields, /*updatedAtValue,*/ ...row }) => row);
  const scrollFields = tableFields.filter((field) => field.key !== 'status' && field.key !== 'escalationTo');
  const stickyFields = [STATUS_FIELD, ESCALATION_FIELD];
  const showLoading = Boolean(
    complaintTypeId &&
    !error &&
    !tableRows.length &&
    (isLoading || !hasLoaded)
  );

  const handleStatusFilterChange = (nextStatus) => {
    setStatusFilter({ complaintTypeId, status: nextStatus });
    if (!complaintTypeId) return;
    dispatch(fetchQueries({
      complaintTypeId,
      page: 1,
      limit: 10,
      status: nextStatus === 'all' ? undefined : nextStatus,
    }));
  };

  const toggleUpdatedAtSortDirection = () => {
    setUpdatedAtSortFilter({
      complaintTypeId,
      direction: getNextUpdatedAtSortDirection(selectedUpdatedAtSort),
    });
  };

  const handleNewComplaint =()=>{
    if (!complaintTypeId) return;
    navigate(`/complaintmanagement/new/${complaintTypeId}`);
  };

  const handleComplaintStatusChange = (row, nextValue) => {
    if (row?.id == null) return;
    if (updatingId != null && String(updatingId) === String(row.id)) return;
    if (String(nextValue) === String(row.statusId) || String(nextValue) === String(row.status)) return;

    const status = statusList.find((item) => (
      String(item.id) === String(nextValue) || String(item.name) === String(nextValue)
    ));
    if (status?.id == null) return;

    const resolveLike = isResolvedOrClosedStatus(status.name);
    if (resolveLike && !row.canResolve) return;
    if (row.isFinalLevel && !resolveLike) return;

    dispatch(changeComplaintStatus({
      id: row.id,
      statusId: status.id,
      currentStepId: row.currentStepId,
      status: status.name,
    }));
  };

  const handleComplaintEscalationChange = (row, nextValue) => {
    if (row?.id == null) return;
    if (updatingId != null && String(updatingId) === String(row.id)) return;
    if (String(nextValue) === String(row.currentStepId) || String(nextValue) === String(row.escalationTo)) return;
    if (row.isFinalLevel) return;

    const nextStep = steps.find((step) => (
      String(step.id) === String(nextValue) || `Level ${step.levelNo}` === String(nextValue)
    ));
    if (nextStep?.id == null) return;

    const current = steps.find((step) => String(step.id) === String(row.currentStepId))
      ?? steps.find((step) => `Level ${step.levelNo}` === row.escalationTo);
    const sortedSteps = [...steps].sort((left, right) => (left.levelNo ?? 0) - (right.levelNo ?? 0));
    if (current && nextStep.levelNo !== current.levelNo + 1) return;
    if (!current && nextStep.levelNo !== sortedSteps[0]?.levelNo) return;

    dispatch(changeComplaintStatus({
      id: row.id,
      statusId: row.statusId,
      currentStepId: nextStep.id,
      status: row.status,
      level: nextStep.levelNo,
    }));
  };

  const getComplaintCellContext = (row) => ({
    statusOptions: getStatusDropdownOptions(statusList, row),
    escalationOptions: getEscalationDropdownOptions(steps, row),
    onStatusChange: (nextValue) => handleComplaintStatusChange(row, nextValue),
    onEscalationChange: (nextValue) => handleComplaintEscalationChange(row, nextValue),
  });
  return (
    <section className="ca-complaint-table" aria-label="Complaint records">
      <div className="ca-complaint-table__toolbar">
        <div className="ca-complaint-table__filters">
          <label className="ca-complaint-table__filter">
            <Filter size={14} aria-hidden="true" />
            <span className="ca-complaint-table__sr-only">Filter by status</span>
            <select
              value={selectedStatus}
              aria-label="Filter by status"
              onChange={(event) => handleStatusFilterChange(event.target.value)}
            >
              <option value="all">Select Status</option>
              {(statusNames.length ? statusNames : fallbackStatusNames).map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className={`ca-complaint-table__sort${selectedUpdatedAtSort === UPDATED_AT_SORT_OLDEST ? ' ca-complaint-table__sort--reversed' : ''}`}
            aria-label={
              selectedUpdatedAtSort === UPDATED_AT_SORT_OLDEST
                ? 'Sort by newest updated at date'
                : 'Sort by oldest updated at date'
            }
            title="Sort by Updated At"
            onClick={toggleUpdatedAtSortDirection}
          >
            <ArrowUpDown
              className="ca-complaint-table__sort-icon"
              size={14}
              aria-hidden="true"
            />
          </button>
        </div>
        <div className="ca-complaint-table__actions">
          <Button variant="outline" onClick={() => handleNewComplaint()} size="sm" startIcon={<Plus size={14} />}>
         
            New Complaint
        
          </Button>
          <Button variant="outline" size="sm" startIcon={<Download size={14} />}>
            Download Report
          </Button>
        </div>
      </div>

      {error ? <p className="ca-body">{error}</p> : null}
      {statusUpdateError ? (
        <div className="ca-validation-toast" role="alert">{statusUpdateError}</div>
      ) : null}
      {showLoading ? (
        <Loading text="Loading complaints" />
      ) : (
      <div className="ca-complaint-table__scroll">
        <table>
          <thead>
            <tr>
              <th scope="col">#</th>
              {scrollFields.map((field) => (
                <th scope="col" key={field.id} className={getComplaintCellClassName(field.key, true)}>{fieldLabel(field)}</th>
              ))}
              <th className="ca-complaint-table__spacer" aria-hidden="true" />
              {stickyFields.map((field) => (
                <th scope="col" key={field.id} className={getComplaintCellClassName(field.key, true)}>{fieldLabel(field)}</th>
              ))}
              {/* <th className="ca-complaint-table__sticky-column ca-complaint-table__sticky-column--status" scope="col">{statusField.label}</th> */}
              {/* <th className="ca-complaint-table__sticky-column ca-complaint-table__sticky-column--escalation" scope="col">{escalationField.label}</th> */}
            </tr>
          </thead>
          <tbody>
            {tableRows.length ? tableRows.map((row, index) => (
              <tr key={row.id ?? index}>
                <td>{index + 1}</td>
                {scrollFields.map((field) => (
                  <td
                    key={`${row.id ?? index}-${field.id}`}
                    className={getComplaintCellClassName(field.key)}
                  >
                    {renderComplaintTableCell(field, row, statusNames, fallbackEscalationLabels, getComplaintCellContext(row))}
                  </td>
                ))}
                <td className="ca-complaint-table__spacer" aria-hidden="true" />
                {stickyFields.map((field) => (
                  <td
                    key={`${row.id ?? index}-${field.id}`}
                    className={getComplaintCellClassName(field.key)}
                  >
                    {renderComplaintTableCell(field, row, statusNames, fallbackEscalationLabels, getComplaintCellContext(row))}
                  </td>
                ))}
                {/* <td className="ca-complaint-table__sticky-column ca-complaint-table__sticky-column--status">
                  <span className="ca-complaint-table__select-value">{row[statusField.key] ?? '-'}</span>
                  <ChevronDown size={14} aria-hidden="true" />
                </td>
                <td className="ca-complaint-table__sticky-column ca-complaint-table__sticky-column--escalation">
                  <span className="ca-complaint-table__select-value">{row[escalationField.key] ?? '-'}</span>
                  <ChevronDown size={14} aria-hidden="true" />
                </td> */}
              </tr>
            )) : (
              <tr className="ca-complaint-table__empty-row">
                <td
                  className="ca-complaint-table__empty"
                  colSpan={1 + scrollFields.length + 1 + stickyFields.length}
                >
                  <EmptyState text="No Complaint" />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      )}
    </section>
  );
}
